import * as THREE from 'three';
import {
  BufferGeometry,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshPhongMaterial,
  Object3D,
  ShapeGeometry,
  Vector3,
} from 'three';
import { Font } from 'three-stdlib';
import { useMemo, type FC, useEffect } from 'react';
import { disposeMaterials } from './Util';

export interface ViewCubeCompassProps {
  ringColor: string;
  letterStyle: { color: string; fontSize: number; strokeColor: string; strokeWidth: number };
}

const compassPosition: [x: number, y: number, z: number] = [0, 0, 1];

export interface ViewCubeCompassExtendedProps extends ViewCubeCompassProps {
  cubeSize: number;
  northRotation: number;
}

// Online font generation
// https://threejs.org/docs/?q=Font#api/en/loaders/FontLoader
// https://gero3.github.io/facetype.js/

const compassLettersFontJson = JSON.parse(
  `{"glyphs":{"E":{"ha":781,"x_min":88,"x_max":750,"o":"m 682 428 l 292 428 l 292 163 l 750 163 l 750 0 l 88 0 l 88 987 l 749 987 l 749 823 l 292 823 l 292 587 l 682 587 l 682 428 z "},"N":{"ha":981,"x_min":88,"x_max":891,"o":"m 891 0 l 688 0 l 292 650 l 292 0 l 88 0 l 88 987 l 292 987 l 688 336 l 688 987 l 891 987 l 891 0 z "},"S":{"ha":854,"x_min":47,"x_max":804,"o":"m 600 259 q 559 348 600 317 q 413 413 519 378 q 245 480 307 447 q 77 725 77 571 q 122 868 77 805 q 252 966 168 930 q 441 1001 336 1001 q 630 963 547 1001 q 759 854 713 924 q 804 696 804 785 l 601 696 q 558 801 601 764 q 438 839 515 839 q 322 807 363 839 q 281 724 281 776 q 329 644 281 676 q 472 583 378 611 q 725 453 646 530 q 804 260 804 376 q 707 59 804 132 q 446 -14 610 -14 q 239 28 332 -14 q 96 142 145 70 q 47 311 47 215 l 251 311 q 446 147 251 147 q 559 177 519 147 q 600 259 600 206 z "},"W":{"ha":1215,"x_min":24,"x_max":1193,"o":"m 857 285 l 990 987 l 1193 987 l 974 0 l 769 0 l 608 661 l 448 0 l 243 0 l 24 987 l 227 987 l 360 286 l 523 987 l 695 987 l 857 285 z "}},"familyName":"Roboto","ascender":1289,"descender":-339,"underlinePosition":-102,"underlineThickness":68,"boundingBox":{"yMin":-376,"xMin":-1009,"yMax":1467,"xMax":1654},"resolution":1000,"original_font_information":{"format":0,"copyright":"Copyright 2011 Google Inc. All Rights Reserved.","fontFamily":"Roboto","fontSubfamily":"Bold","uniqueID":"Roboto Bold","fullName":"Roboto Bold","version":"Version 2.137; 2017","postScriptName":"Roboto-Bold","trademark":"Roboto is a trademark of Google.","designer":"Google","manufacturerURL":"Google.com","designerURL":"Christian Robertson","licence":"Licensed under the Apache License, Version 2.0","licenceURL":"http://www.apache.org/licenses/LICENSE-2.0"},"cssFontWeight":"bold","cssFontStyle":"normal"}`
);

const compassLettersData = [
  // { letter: 'N', rotation: [0, Math.PI, 0], position: [0, -1, 0] },
  // { letter: 'S', rotation: [0, Math.PI, Math.PI], position: [0, 1, 0] },
  // { letter: 'E', rotation: [0, Math.PI, -Math.PI / 2], position: [1, 0, 0] },
  // { letter: 'W', rotation: [Math.PI, 0, -Math.PI / 2], position: [-1, 0, 0] },
  { letter: 'S', rotation: [Math.PI, 0, 0], position: [0, 1, 0], anchor: [-0.5, -1] },
  { letter: 'N', rotation: [Math.PI, 0, 0], position: [0, -1, 0], anchor: [-0.5, 0] },
  { letter: 'E', rotation: [Math.PI, 0, 0], position: [1, 0, 0], anchor: [0, -0.5] },
  { letter: 'W', rotation: [Math.PI, 0, 0], position: [-1, 0, 0], anchor: [-1, -0.5] },
];

export const ViewCubeCompass: FC<ViewCubeCompassExtendedProps> = ({
  cubeSize,
  ringColor,
  letterStyle,
  northRotation,
}) => {
  const font = useMemo(() => {
    return new Font(compassLettersFontJson);
  }, []);

  const letterObjects = useMemo(() => {
    const { fontSize, color, strokeColor, strokeWidth } = letterStyle;

    const letterMaterial = new MeshPhongMaterial({
      color,
      side: THREE.DoubleSide,
      // TODO: transparency doesn't work for all materials (probably in secondary scene?)
      // opacity: 0.4,
      // transparent: true,
    });

    const strokeMaterial = new LineBasicMaterial({
      color: strokeColor,
      linewidth: strokeWidth,
      side: THREE.DoubleSide,
      //   opacity: 0.5,
      //   transparent: true,
    });

    return compassLettersData.map(({ letter, rotation, position, anchor }) => {
      const shapes = font.generateShapes(letter, fontSize);

      const letterGeometry = new ShapeGeometry(shapes);
      letterGeometry.computeBoundingBox();
      const bbox = letterGeometry.boundingBox!;
      const xSize = bbox.max.x - bbox.min.x;
      const ySize = bbox.max.y - bbox.min.y;

      const anchorVec = new Vector3().set(anchor[0] * xSize, anchor[1] * ySize, 0);

      letterGeometry.translate(anchorVec.x, anchorVec.y, 0);

      const scale = (60 * cubeSize + fontSize) / 60;

      const letterMesh = new Mesh(letterGeometry, letterMaterial);
      letterMesh.rotation.set(...(rotation as Parameters<Vector3['set']>));
      letterMesh.position.set(...(position as Parameters<Vector3['set']>));
      letterMesh.position.multiplyScalar(scale);

      const letterStroke = new Object3D();
      letterStroke.rotation.set(...(rotation as Parameters<THREE.Vector3['set']>));
      letterStroke.position.set(...(position as Parameters<THREE.Vector3['set']>));
      letterStroke.position.multiplyScalar(scale);

      shapes.forEach((shape) => {
        const points = shape.getPoints();
        const geometry = new BufferGeometry().setFromPoints(points);
        geometry.translate(anchorVec.x, anchorVec.y, 0);
        const line = new Line(geometry, strokeMaterial);
        letterStroke.add(line);
      });

      return { letterMesh, letterStroke };
    });
  }, [cubeSize, font, letterStyle]);

  useEffect(() => {
    return () => {
      letterObjects.forEach(({ letterMesh, letterStroke }) => {
        letterMesh.geometry.dispose();
        disposeMaterials(letterMesh.material);

        letterStroke.children.forEach((child) => {
          (child as Line).geometry.dispose();
          disposeMaterials((child as Line).material);
        });
      });
    };
  }, [letterObjects]);

  const letterObjectsComp = useMemo(
    () =>
      letterObjects.map(({ letterMesh, letterStroke }, index) => {
        const { letter } = compassLettersData[index];
        return (
          <group key={`${letter}`}>
            <primitive object={letterMesh} />
            <primitive object={letterStroke} />
          </group>
        );
      }),
    [letterObjects]
  );

  const rotationMemo = useMemo<[x: number, y: number, z: number]>(
    () => [Math.PI / 2, 0, northRotation],
    [northRotation]
  );

  const positionMemo = useMemo<[x: number, y: number, z: number]>(
    () => [0, -0.5 * cubeSize, 0],
    [cubeSize]
  );

  const ringGeometryArgumentsMemo = useMemo<[x: number, y: number, z: number]>(
    () => [(50 * cubeSize) / 60, (56 * cubeSize) / 60, 128],
    [cubeSize]
  );

  return (
    <group key="compass_group" rotation={rotationMemo} position={positionMemo}>
      <mesh key="compass_ring" position={compassPosition}>
        <ringGeometry attach="geometry" args={ringGeometryArgumentsMemo} />
        <meshPhongMaterial attach="material" color={ringColor} side={THREE.DoubleSide} />
        {letterObjectsComp}
      </mesh>
    </group>
  );
};
